<template>
    <div class="apprentice-simple-card badges-overview">
        <div class="description">
            {{ $t('admin.for_optimal_results_only_images_with_a_transparent_background_are_allowed') }}<br />
            {{ $t('admin.the_images_must_be_square_and_not_larger_than_200x200_with_a_maximum_size_of_50kb') }}
        </div>

        <div
            class="error"
            v-if="hasError">
            <b-alert
                show
                variant="danger">
                {{ $t('admin.one_or_more_files_have_been_found_larger_than_50kb_these_files_have_been_skipped') }}
            </b-alert>
        </div>

        <design-2021-apprentice-data-table
            class="badges-overview-table"
            :items="badges"
            :fields="fields"
            :custom-per-page="50"
            nosearch
            show-empty>
            <template #options>
                <b-button
                    class="btn-upload btn-badge-upload"
                    variant="primary"
                    @click="$refs.badgesUpload.$el.childNodes[0].click()"
                    >{{ $t('admin.add_badge') }}
                </b-button>
                <b-form-file
                    id="badge-file-input"
                    v-model="files"
                    v-show="false"
                    accept="image/png"
                    multiple
                    size=""
                    :placeholder="'badge'"
                    ref="badgesUpload"
                    @change="onFilesChange">
                </b-form-file>
            </template>

            <template #cell(name)="row">
                <div class="badge-name">
                    {{ row.item.name }}
                </div>
            </template>

            <template #cell(preview)="row">
                <div class="badge-preview">
                    <img
                        :src="row.item.file.public_path"
                        :alt="row.item.name" />
                </div>
            </template>

            <template #cell(action)="row">
                <div class="row-actions">
                    <b-button
                        class="btn-remove btn-remove-badge"
                        v-b-tooltip.hover
                        variant="danger-icon-only"
                        :title="$t('common.delete')"
                        @click="removeBadge(row.item.id)">
                    </b-button>
                </div>
            </template>

            <template #empty>
                <div class="not-found">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">
                        {{ $t('admin.no_badges_found') }}
                    </div>
                </div>
            </template>
        </design-2021-apprentice-data-table>
    </div>
</template>

<script>
export default {
    name: 'design-2021-admin-badges-index-component',
    props: {
        badgesIn: {
            type: Array,
            required: true,
            default: [],
        },
    },

    data() {
        return {
            badges: this.badgesIn,
            fields: [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'preview',
                    label: this.$t('common.badge'),
                },
                {
                    key: 'action',
                    label: this.$t('common.actions'),
                },
            ],
            files: null,
            hasError: false,
        };
    },

    methods: {
        onFilesChange(evt) {
            this.storeBadges(evt.target.files);
        },

        storeBadges(files) {
            this.hasError = false;

            if (files === null) {
                return;
            }

            const data = new FormData();
            const options = {
                headers: { 'Content-Type': 'multipart/form-data' },
            };
            let needle = 0;

            for (const file of files) {
                if (file.size <= 50 * 1024) {
                    data.append('badge[' + needle + ']', file);
                    needle++;
                } else {
                    this.hasError = true;
                }
            }

            this.alert.saving();
            this.axios.post(this.routes.addAdminBadges(), data, options).then(
                (response) => {
                    this.badges = [...response.data];
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        removeBadge(badgeId) {
            this.alert
                .confirm(
                    this.$t('admin.delete_badge'),
                    this.$t('admin.are_you_sure_you_want_to_delete_this_badge'),
                    this.$t('common.yes_delete'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.axios
                        .delete(this.routes.removeAdminBadge(badgeId))
                        .then(() => {
                            this.badges = this.badges.filter((badge) => badge.id !== badgeId);
                            this.alert.success();
                        })
                        .catch((error) => {
                            if (error.response.status === 405) {
                                this.alert.error(
                                    this.$t('admin.badge_is_not_deleted'),
                                    this.$t('admin.this_badge_cannot_be_deleted_because_it_is_in_use'),
                                );
                                return;
                            }

                            this.alert.ajaxError(error);
                        });
                });
        },
    },

    watch: {
        badges: function (newVal) {
            this.badges = newVal;
        },
    },
};
</script>

<style scoped lang="scss">
.description {
    font-size: $base-font-size;
    line-height: 1.5;
    color: $text-muted-color;
    font-weight: $font-weight-bold;
    margin-bottom: map-get($grid-gutter-spacing, 'sixty');
}

.badge-name {
    color: $text-muted-color;
    font-weight: $font-weight-bold;
}

.badge-preview {
    display: flex;
    align-self: center;
    justify-content: center;
    background-color: $white;
    background-clip: border-box;
    height: 70px;
    width: 70px;
    padding: map-get($grid-gutter-spacing, 'ten');
    border-radius: 50%;
    border: 3px solid var(--primary);

    .img {
        width: 95%;
        height: 95%;
        object-position: center;
        object-fit: contain;
    }
}
</style>
